import type { CompoundEntityRef, Entity } from '@backstage/catalog-model';
import type { KnownKinds } from '@ipen-portal/plugin-common';

import { parseEntityRef, stringifyEntityRef } from '@backstage/catalog-model';
import { useApi } from '@backstage/frontend-plugin-api';
import { catalogApiRef } from '@backstage/plugin-catalog-react';
import { useQuery } from '@tanstack/react-query';

export const useGetEntitiesByRef = <T extends Entity>(
  entityRefs:
    | null
    | readonly (CompoundEntityRef | null | string | undefined)[]
    | undefined,
  options?: {
    fallbackKind?: KnownKinds;
    predicate?: (entity: Entity) => entity is T;
    skip?: boolean;
  },
) => {
  const catalogApi = useApi(catalogApiRef);
  const { fallbackKind, predicate, skip } = options ?? {};

  return useQuery({
    enabled: !!entityRefs?.some(ref => ref != null) && !skip,
    gcTime: 1000 * 60 * 5,
    queryFn: async () => {
      const refs = (entityRefs ?? []).map(ref =>
        (ref == null ? undefined : stringifyEntityRef(parseEntityRef(ref, { defaultKind: fallbackKind }))),
      );
      const { items } = await catalogApi.getEntitiesByRefs({
        entityRefs: refs.filter((ref): ref is string => ref !== undefined),
      });
      let index = 0;
      return refs.map(ref => (ref === undefined ? undefined : items[index++]));
    },
    queryKey: ['catalog', 'entitiesByRefs', entityRefs, { fallbackKind }],
    select: (entities): (T | undefined)[] =>
      entities.map(entity =>
        (entity && (!predicate || predicate(entity)) ? entity as T : undefined),
      ),
    staleTime: 1000 * 60 * 5,
  });
};
